// ─────────────────────────────────────────────────────────────────
// Loyalty badge tiers — single source of truth.
//
// A user's tier is driven by their number of DELIVERED orders
// (see `order_count` on UserOut / GET /auth/me). The badge art lives in
// uploads/badges/ and is served at /uploads/badges/*.webp.
// ─────────────────────────────────────────────────────────────────

export interface BadgeTier {
  slug: string;
  /** Display name. */
  name: string;
  /** Devanagari spelling (kept for reference; no longer displayed). */
  hindi: string;
  /** Latin-style meaning shown beside the name. */
  meaning: string;
  /** Motivational tagline for the tier. */
  tagline: string;
  /** Public image URL. */
  src: string;
  /** Accent colour for the glow halo / ring. */
  glow: string;
  /** Inclusive lower bound of delivered orders. */
  min: number;
  /** Inclusive upper bound (Infinity for the top tier). */
  max: number;
  /** Cashback earn rate for this rank (fraction of order value). */
  rate: number;
}

const base = "/uploads/badges";

export const BADGE_TIERS: BadgeTier[] = [
  { slug: "aagman", name: "Aagman", hindi: "आगमन", meaning: "Novus",  tagline: "The journey begins.",            src: `${base}/Aagman_Badge.webp`, glow: "#34d399", min: 1,  max: 5,        rate: 0.01 },
  { slug: "maanav", name: "Maanav", hindi: "मानव", meaning: "Sapien", tagline: "Taking charge of your choices.", src: `${base}/Maanav_Badge.webp`, glow: "#2dd4bf", min: 6,  max: 15,       rate: 0.015 },
  { slug: "daiv",   name: "Daiv",   hindi: "दैव",   meaning: "Deus",   tagline: "Rising above the smoke.",         src: `${base}/Daiv_Badge.webp`,   glow: "#4ade80", min: 16, max: 25,       rate: 0.02 },
  { slug: "daitya", name: "Daitya", hindi: "दैत्य", meaning: "Titan",  tagline: "Unstoppable force.",             src: `${base}/Daitya_Badge.webp`, glow: "#22c55e", min: 26, max: 50,       rate: 0.025 },
  { slug: "asur",   name: "Asur",   hindi: "असुर",  meaning: "Daimōn", tagline: "Power beyond limits.",           src: `${base}/Asur_Badge.webp`,   glow: "#16a34a", min: 51, max: Infinity, rate: 0.03 },
];

/** The tier for a given delivered-order count, or null if the user has none yet. */
export function getBadgeForOrders(count: number): BadgeTier | null {
  if (!count || count < 1) return null;
  return BADGE_TIERS.find((t) => count >= t.min && count <= t.max) ?? null;
}

/** Cashback earn rate (fraction) for a given delivered-order count; 0 if no rank yet. */
export function cashbackRate(count: number): number {
  return getBadgeForOrders(count)?.rate ?? 0;
}

/**
 * The next tier to aim for and how many more delivered orders are needed,
 * or null if the user is already at the top tier. Works for count 0 too
 * (returns the first tier).
 */
export function nextTier(count: number): { tier: BadgeTier; remaining: number } | null {
  const c = Math.max(0, count || 0);
  const upcoming = BADGE_TIERS.find((t) => t.min > c);
  if (!upcoming) return null;
  return { tier: upcoming, remaining: upcoming.min - c };
}
